type WebAudioWindow = Window & {
  webkitAudioContext?: typeof AudioContext;
};

// Chime ascendente La5 -> Re6. Cada nota lleva su propia envolvente para que la
// segunda suene al mismo volumen que la primera: antes se cambiaba la frecuencia
// de un único oscilador mientras el volumen ya venía cayendo, así que la segunda
// nota quedaba ~14 dB por debajo y en la práctica se escuchaba un solo blip.
const NOTA_1_HZ = 880;
const NOTA_2_HZ = 1174.66;

// `triangle` en vez de `sine`: una sinusoidal pura no tiene armónicos y es la onda
// que más fácil se enmascara con ruido ambiente, sobre todo en parlantes chicos de
// notebook o celular, que dependen de los armónicos para tener presencia.
const TIPO_ONDA: OscillatorType = "triangle";

const ATAQUE_S = 0.015;
const GANANCIA_MINIMA = 0.0001;

// Si el audio estaba bloqueado, no reproducir un aviso viejo: sonar por un correo
// de hace 20 minutos apenas el usuario toca algo confunde más de lo que ayuda.
const PENDIENTE_TTL_MS = 30000;

let audioContext: AudioContext | null = null;
let sonidoPendienteAt: number | null = null;

function getAudioContext(): AudioContext | null {
  if (audioContext) return audioContext;
  const AudioContextCtor =
    window.AudioContext || (window as WebAudioWindow).webkitAudioContext;
  if (!AudioContextCtor) return null;
  audioContext = new AudioContextCtor();
  return audioContext;
}

function tocarNota(
  context: AudioContext,
  frecuencia: number,
  inicio: number,
  duracion: number,
  pico: number
): void {
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = TIPO_ONDA;
  oscillator.frequency.setValueAtTime(frecuencia, inicio);

  gain.gain.setValueAtTime(GANANCIA_MINIMA, inicio);
  gain.gain.exponentialRampToValueAtTime(pico, inicio + ATAQUE_S);
  gain.gain.exponentialRampToValueAtTime(GANANCIA_MINIMA, inicio + duracion);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(inicio);
  oscillator.stop(inicio + duracion + 0.02);
}

/** Devuelve true si el sonido llegó a reproducirse. */
export async function playMailSound(): Promise<boolean> {
  const context = getAudioContext();
  if (!context) return false;

  try {
    if (context.state === "suspended") {
      await context.resume();
    }
    if (context.state !== "running") {
      sonidoPendienteAt = Date.now();
      return false;
    }

    const inicio = context.currentTime;
    tocarNota(context, NOTA_1_HZ, inicio, 0.22, 0.32);
    tocarNota(context, NOTA_2_HZ, inicio + 0.13, 0.5, 0.36);

    sonidoPendienteAt = null;
    return true;
  } catch {
    // El navegador puede bloquear audio si aún no hubo interacción del usuario.
    sonidoPendienteAt = Date.now();
    return false;
  }
}

/**
 * Reintenta un sonido que quedó bloqueado por falta de interacción del usuario,
 * siempre que siga siendo reciente.
 */
export async function flushPendingMailSound(): Promise<void> {
  if (sonidoPendienteAt === null) return;

  const vencido = Date.now() - sonidoPendienteAt > PENDIENTE_TTL_MS;
  sonidoPendienteAt = null;
  if (vencido) return;

  await playMailSound();
}

/** Se llama ante cualquier gesto del usuario para destrabar el audio. */
export async function unlockMailAudio(): Promise<void> {
  const context = getAudioContext();
  if (!context) return;
  try {
    if (context.state === "suspended") {
      await context.resume();
    }
    await flushPendingMailSound();
  } catch {
    // Sin audio disponible: el badge y la notificación siguen funcionando.
  }
}
